import { initApolloClient } from './apollo-client'

import { CookieDocument } from '~generated/hooks'
import { getIsomorphicCookie, isBrowser, isEmpty } from '~lib/helpers'
import { ExtendedNextPage, ExtendedNextPageContext } from '~types/api/next'

/**
 * HOC to handle cases:
 * 1. Inject apollo client instance to next.js context
 * 3. Save cookie to apollo cache
 */
const withApollo = (PageComponent: ExtendedNextPage<any>): ExtendedNextPage<any> => {
  const WithApollo = (props: ObjectType) => {
    return <PageComponent {...props} />
  }

  if (process.env.NODE_ENV !== 'production') {
    const name = PageComponent.displayName || PageComponent.name || 'Component'
    WithApollo.displayName = `withApollo(${name})`
  }

  if (PageComponent.getInitialProps) {
    WithApollo.getInitialProps = async (ctx: ExtendedNextPageContext) => {
      // prevent calling for source maps
      const pathname = isBrowser() ? window.location.pathname : ctx.req.url
      if (pathname.endsWith('.map')) {
        return {}
      }
      const apolloClient = initApolloClient(null, ctx)
      const cookie = getIsomorphicCookie(ctx?.req)

      if (!isEmpty(cookie)) {
        apolloClient.cache.writeQuery({
          query: CookieDocument,
          data: { cookie },
        })
      }

      ctx.apolloClient = apolloClient
      const pageProps = await PageComponent.getInitialProps(ctx)

      return { ...pageProps, apolloState: apolloClient.cache.extract() }
    }
  }

  return WithApollo
}

export default withApollo
